import { useState, useEffect, useCallback, useRef } from 'react';

// Types for asset loading
interface AssetLoadState {
  loading: boolean;
  loaded: boolean;
  error: Error | null;
  data: string | null;
}

interface LazyAssetOptions {
  preload?: boolean;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
}

// Hook for lazy loading SVG assets
export const useLazySVG = (
  assetPath: string,
  options: LazyAssetOptions = {}
): AssetLoadState & { retry: () => void } => {
  const {
    preload = false,
    timeout = 5000,
    retryAttempts = 3,
    retryDelay = 1000,
  } = options;

  const [state, setState] = useState<AssetLoadState>({
    loading: false,
    loaded: false,
    error: null,
    data: null,
  });

  const retryCountRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadAsset = useCallback(async () => {
    if (state.loaded || state.loading) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      // Set timeout
      timeoutRef.current = setTimeout(() => {
        abortControllerRef.current?.abort();
      }, timeout);

      const response = await fetch(assetPath, {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load asset: ${response.status} ${response.statusText}`);
      }

      const svgContent = await response.text();

      // Clear timeout on success
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setState({
        loading: false,
        loaded: true,
        error: null,
        data: svgContent,
      });

      retryCountRef.current = 0;
    } catch (error) {
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const isAborted = error instanceof Error && error.name === 'AbortError';
      const errorMessage = isAborted ? 'Request timed out' : (error as Error).message;

      if (retryCountRef.current < retryAttempts && !isAborted) {
        retryCountRef.current++;
        setTimeout(() => {
          setState(prev => ({ ...prev, loading: false }));
          loadAsset();
        }, retryDelay * retryCountRef.current);
      } else {
        setState({
          loading: false,
          loaded: false,
          error: new Error(errorMessage),
          data: null,
        });
      }
    }
  }, [assetPath, timeout, retryAttempts, retryDelay, state.loaded, state.loading]);

  const retry = useCallback(() => {
    retryCountRef.current = 0;
    setState({
      loading: false,
      loaded: false,
      error: null,
      data: null,
    });
    loadAsset();
  }, [loadAsset]);

  useEffect(() => {
    if (preload) {
      loadAsset();
    }

    return () => {
      // Cleanup on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [preload, loadAsset]);

  return {
    ...state,
    retry,
  };
};

// Hook for intersection observer (lazy loading on scroll)
export const useIntersectionObserver = (
  options: IntersectionObserverInit = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const targetRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { threshold = 0.1, rootMargin = '50px', ...restOptions } = options;

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setIsIntersecting(isVisible);
        
        if (isVisible && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold,
        rootMargin,
        ...restOptions,
      }
    );

    observerRef.current.observe(target);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin, hasIntersected, restOptions]);

  return {
    targetRef,
    isIntersecting,
    hasIntersected,
  };
};

// Combined hook for lazy loading assets with intersection observer
export const useLazyAssetWithIntersection = (
  assetPath: string,
  assetOptions: LazyAssetOptions = {},
  intersectionOptions: IntersectionObserverInit = {}
) => {
  const { targetRef, hasIntersected } = useIntersectionObserver(intersectionOptions);
  const assetState = useLazySVG(assetPath, {
    ...assetOptions,
    preload: hasIntersected || assetOptions.preload,
  });

  return {
    targetRef,
    hasIntersected,
    ...assetState,
  };
};

// Hook for preloading multiple assets
export const useAssetPreloader = (assetPaths: string[]) => {
  const [loadedAssets, setLoadedAssets] = useState<Set<string>>(new Set());
  const [failedAssets, setFailedAssets] = useState<Set<string>>(new Set());
  const [isPreloading, setIsPreloading] = useState(false);

  const preloadAssets = useCallback(async () => {
    if (assetPaths.length === 0) return;

    setIsPreloading(true);
    const loaded = new Set<string>();
    const failed = new Set<string>();

    const promises = assetPaths.map(async (path) => {
      try {
        const response = await fetch(path);
        if (response.ok) {
          loaded.add(path);
        } else {
          failed.add(path);
        }
      } catch {
        failed.add(path);
      }
    });

    await Promise.allSettled(promises);

    setLoadedAssets(loaded);
    setFailedAssets(failed);
    setIsPreloading(false);
  }, [assetPaths]);

  useEffect(() => {
    preloadAssets();
  }, [preloadAssets]);

  return {
    loadedAssets,
    failedAssets,
    isPreloading,
    retry: preloadAssets,
    progress: {
      total: assetPaths.length,
      loaded: loadedAssets.size,
      failed: failedAssets.size,
      percentage: assetPaths.length > 0 ? (loadedAssets.size / assetPaths.length) * 100 : 0,
    },
  };
};

// Asset cache for better performance
class AssetCache {
  private cache = new Map<string, string>();
  private maxSize: number;

  constructor(maxSize = 50) {
    this.maxSize = maxSize;
  }

  get(key: string): string | undefined {
    return this.cache.get(key);
  }

  set(key: string, value: string): void {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

// Global asset cache instance
export const assetCache = new AssetCache();

// Hook with caching support
export const useCachedSVG = (
  assetPath: string,
  options: LazyAssetOptions = {}
): AssetLoadState & { retry: () => void } => {
  const { retry, ...assetState } = useLazySVG(assetPath, options);

  useEffect(() => {
    if (assetState.loaded && assetState.data && !assetCache.has(assetPath)) {
      assetCache.set(assetPath, assetState.data);
    }
  }, [assetState.loaded, assetState.data, assetPath]);

  // Use cached data if available, otherwise use hook state
  const finalState = assetCache.has(assetPath) ? {
    loading: false,
    loaded: true,
    error: null,
    data: assetCache.get(assetPath)!,
  } : assetState;

  return {
    ...finalState,
    retry,
  };
};